import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import BaseWidget from '../BaseWidget';
import { TableComponent } from '../../TableComponent_v2';
import useWidgetData from '../hooks/useWidgetData';
import useWidgetConfig from '../hooks/useWidgetConfig';
import { transformWidgetData } from '../config';
import WidgetEmptyState from '../WidgetEmptyState';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { COST_TABLE_MEASURES, RESOURCE_CHANGES_TABLE_MEASURES, DEFAULT_TABLE_MEASURES, DEFAULT_RESOURCE_CHANGES_TABLE_MEASURES } from '../../table/measureRegistry';

// Pre-define icons at module level (babel macro requires static values)
const tableIcon = icon({ name: 'table', family: 'sharp', style: 'light' });

/**
 * Table Widget - Wraps the existing TableComponent with widget functionality
 * Supports both static and API data sources
 * Uses the useWidgetConfig hook for centralized config handling
 */
const TableWidget = ({ 
  widgetId, 
  config, 
  filters = [], 
  onRemove, 
  isEditMode = false,
  onFilterAdd 
}) => {
  // Detect if this is a resource changes table by checking the endpoint
  const isResourceChangesTable = useMemo(() => {
    return config?.dataSource?.endpoint === "/get-resource-changes";
  }, [config?.dataSource?.endpoint]);

  // Get the appropriate measures registry and default measures
  const measuresRegistry = isResourceChangesTable ? RESOURCE_CHANGES_TABLE_MEASURES : COST_TABLE_MEASURES;
  const defaultMeasures = isResourceChangesTable ? DEFAULT_RESOURCE_CHANGES_TABLE_MEASURES : DEFAULT_TABLE_MEASURES;

  // Check if any selected measures need trend data
  const needsTrendData = useMemo(() => {
    const selectedMeasures = Array.isArray(config?.measures) && config.measures.length > 0 
      ? config.measures 
      : defaultMeasures;
    
    return selectedMeasures.some(measureId => {
      const measure = measuresRegistry[measureId];
      return measure && measure.needsTrendData === true;
    });
  }, [config?.measures, measuresRegistry, defaultMeasures]);

  // Use centralized config hook for date/filter handling
  const { finalConfig, stableFilters } = useWidgetConfig(
    config, 
    filters, 
    { 
      includeDates: true,
      addDetailedResults: false,
      addTrendData: needsTrendData
    }
  );

  const { data, isLoading, error } = useWidgetData(finalConfig, stableFilters);

  // Transform data if it comes from API
  const widgetData = data ? transformWidgetData('table', data) : null;

  // Handle row click events for filtering
  const handleRowClick = (rowData) => {
    if (onFilterAdd && config.onRowClick) {
      const filter = {
        category: config.grouping,
        category_display: config.grouping,
        term: rowData[config.grouping] || rowData.name || rowData.label
      };
      onFilterAdd(filter);
    }
  };

  // Determine empty state content
  const emptyStateContent = isResourceChangesTable 
    ? {
        title: "No resource changes found",
        description: "Try adjusting your date range or filters to see resource changes"
      }
    : {
        title: "No data available",
        description: "Try adjusting your filters or date range"
      };

  return (
    <BaseWidget
      widgetId={widgetId}
      title={config.title}
      isLoading={isLoading}
      error={error}
      onRemove={onRemove}
      isEditMode={isEditMode}
    >
      {widgetData && widgetData.data && widgetData.data.length > 0 ? (
        <TableComponent
          data={widgetData.data}
          grouping={config.grouping || widgetData.grouping}
          title={config.title}
          valueName={config.valueName}
          showTitle={config.showTitle}
          measures={Array.isArray(config?.measures) && config.measures.length > 0 ? config.measures : defaultMeasures}
          endpoint={config?.dataSource?.endpoint}
          tableType={isResourceChangesTable ? "resource-changes" : undefined}
          onRowClick={handleRowClick}
          showIcons={config.showIcons === true}
          showTotalRow={config.showTotalRow === true}
        />
      ) : !isLoading && !error && (
        <WidgetEmptyState 
          title={emptyStateContent.title}
          description={emptyStateContent.description}
          icon={tableIcon}
        />
      )}
    </BaseWidget>
  );
};

TableWidget.propTypes = {
  widgetId: PropTypes.string.isRequired,
  config: PropTypes.shape({
    title: PropTypes.string,
    grouping: PropTypes.string.isRequired,
    valueName: PropTypes.string,
    measures: PropTypes.arrayOf(PropTypes.string),
    showTitle: PropTypes.bool,
    showIcons: PropTypes.bool,
    showTotalRow: PropTypes.bool,
    onRowClick: PropTypes.func,
    dataSource: PropTypes.shape({
      type: PropTypes.oneOf(['static', 'api']).isRequired,
      data: PropTypes.any,
      endpoint: PropTypes.string,
      method: PropTypes.string,
      body: PropTypes.object,
      transform: PropTypes.func
    }).isRequired
  }).isRequired,
  filters: PropTypes.array,
  onRemove: PropTypes.func,
  isEditMode: PropTypes.bool,
  onFilterAdd: PropTypes.func
};

export default TableWidget;
